import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import useAuth from '../global/useAuth';
import usePaymentFlow from './usePaymentFlow';
import { StripeCustomerSessionCache, customerSessionKey } from '../../services/helpers/StripeCustomerSessionCache';
import { preconnectStripe } from '../../utils/stripe';
import { PaymentGateway } from '../../services/exports/Constants';

/**
 * Gets the Stripe Elements checkout ready on the checkout step, while the customer is still filling it
 * in, so the payment screen draws its payment block at once: the connections to Stripe are opened, and
 * a logged-in customer's CustomerSession is asked for, which the payment screen then creates its
 * Elements with on its first render (useStripeCustomerSession reads the kept answer) instead of one
 * more backend round trip later.
 */
export default function useWarmUpStripeElements(): void {
  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const customerId = useSelector((state: StoreInterface) => state.profile.profile?.id);
  const { isLoggedIn } = useAuth();
  const { hasOnlinePayments, onlinePaymentsGateway } = usePaymentFlow();
  const isStripeCheckout = hasOnlinePayments && onlinePaymentsGateway === PaymentGateway.Stripe;

  useEffect(() => {
    if (isStripeCheckout) {
      preconnectStripe();
    }
  }, [isStripeCheckout]);

  useEffect(() => {
    if (!isLoggedIn || !isStripeCheckout) {
      return;
    }

    StripeCustomerSessionCache.prefetch(customerSessionKey(company?.id, customerId), company?.id);
  }, [isLoggedIn, isStripeCheckout, company?.id, customerId]);
}
